'use client';

import Link from 'next/link';
import React, { useCallback } from 'react';
import Button from '../form/Button';
import Form from '@dr-pam/common-components/Components/Form/Form';
import FormControl from '@dr-pam/common-components/Components/Form/FormControl';
import TextInput from '@dr-pam/common-components/Components/Form/TextInput';
import { useForm, schemaResolver } from '@mantine/form';
import z from 'zod';
import { useModalProvider } from '@dr-pam/common-components/Components/Modal/ModalProvider';
import type { ModalProps } from '@dr-pam/common-components/Components/Modal/Modal';
import NewsletterDetailsModalContent from './NewsletterDetailsModal';

type SubscribeFormProps = {
	className?: string;
	buttonClassName?: string;
	inputClassName?: string;
	hideCopy?: boolean;
	buttonText?: string;
};

type SubscribeEmailOnlyFormValues = {
	email: string;
};

const footerEmailSchema = z.object({
	email: z.string().email('Invalid email address').nonempty('Email is required'),
});

export default function SubscribeForm({
	className,
	buttonClassName,
	inputClassName,
	hideCopy,
	buttonText = 'Subscribe',
}: SubscribeFormProps) {
	const btnClass = buttonClassName ?? 'solid dark outline reverse small';
	const modalProvider = useModalProvider();

	const emailForm = useForm<SubscribeEmailOnlyFormValues>({
		initialValues: { email: '' },
		validate: schemaResolver(footerEmailSchema),
	});

	const openNewsletterDetailsModal = useCallback(
		(email: string) => {
			const modalId = 'newsletter-details-modal';

			const modalProps: ModalProps = {
				modalId,
				className: 'popup-overlay blur',
				title: 'Thanks! just a few more details.',
				showClose: true,
				onClose: () => modalProvider.closeModal(modalId),
				children: (
					<NewsletterDetailsModalContent
						initialEmail={email}
						onCompleted={() => {
							emailForm.setValues({ email: '' });
							emailForm.clearErrors();
							emailForm.resetDirty();
							modalProvider.closeModal(modalId);
						}}
						onClose={() => modalProvider.closeModal(modalId)}
					/>
				),
			};

			modalProvider.openModal(modalProps);
		},
		[emailForm, modalProvider],
	);

	const handleOpenModal = () => {
		const validationResult = emailForm.validate();
		if (validationResult.hasErrors) {
			return;
		}

		openNewsletterDetailsModal(emailForm.values.email.trim());
	};

	return (
		<Form form={emailForm} className={`subscribe ${className ?? ''}`} onSubmit={handleOpenModal}>
			{!hideCopy && (
				<div className="text-content">
					<h3>Possums in your inbox</h3>
					<p>Evidence-based insights, tips, and tools. Occasional updates and offers.</p>
				</div>
			)}

			<div className="cta">
				<FormControl name="email" label="Email" className={inputClassName}>
					<TextInput type="email" placeholder="Email" autoComplete="email" />
				</FormControl>

				<Button type="submit" className={btnClass}>
					{buttonText}
				</Button>
			</div>

			<p className="subscribe__fine-print">
				By subscribing you agree to receive emails from Possums. Unsubscribe anytime. See our{' '}
				<Link href="/privacy-policy">Privacy Policy</Link> for more.
			</p>
		</Form>
	);
}
